/**
 * The account's pass in the account dialog (D166): the shell's stateful
 * half of one line and one button.
 *
 * @remarks
 * Asked of the store once the account is known: the account's own pass,
 * and — when there is none to hold — what the pass costs, so the button
 * can say its number. A holder's button is Stripe's Customer Portal (the
 * customer they already are; cancel, resume, the card, the invoices); an
 * account with no pass now gets the checkout, EXCEPT one whose last
 * payment is still being retried, which gets the portal, because a second
 * subscription beside a `past_due` one is the double charge every other
 * line here promises against. Refusals are said through the dialog's own
 * notice. A store that refuses to describe the pass (none offered) leaves
 * the panel off: nothing true to say, nothing said.
 *
 * @packageDocumentation
 */

import type { PassDescription, PassHolding } from '@orpheus/api-contracts';
import type { AccountNotice, PassPanelView } from '@orpheus/player-ui';

import { useEffect, useState } from 'react';

import { type CheckoutOutcome, type StoreClient } from './store-client.js';
import { CHECKOUT_REFUSED, PASS_MANAGE_LABEL, passLabelFor, passLineOf } from './store-copy.js';

/** What the hook needs. */
export interface PassPanelOptions {
	/** The signed-in account's id; absent, nothing is asked and nothing is shown. */
	readonly accountId: string | undefined;
	/** Where the browser goes: Stripe's page. */
	readonly navigate: (url: string) => void;
	/** Say a refusal through the dialog's notice (D172: the kind colors it). */
	readonly say: (notice: AccountNotice) => void;
	/** The store; absent, nothing is shown. */
	readonly storefront: StoreClient | undefined;
}

/** What the store said, once asked. */
interface PassAnswer {
	readonly holding: PassHolding;
	readonly pass: PassDescription | undefined;
}

/**
 * The pass panel for the account dialog.
 *
 * @param options - {@link PassPanelOptions}
 * @returns The panel, or nothing to show
 */
export const usePassPanel = ({ accountId, navigate, say, storefront }: PassPanelOptions): PassPanelView | undefined => {
	const [answer, setAnswer] = useState<PassAnswer | undefined>(undefined);
	const [isBusy, setIsBusy] = useState(false);

	useEffect(() => {
		setAnswer(undefined);

		if (storefront === undefined || accountId === undefined) {
			return undefined;
		}

		let isDisposed = false;

		void storefront.passHolding().then(async (answered) => {
			if (answered !== null && 'refusal' in answered) {
				return;
			}

			const holding: PassHolding = answered;

			// The price is asked for only when the button will need it: no pass held now, and not a payment being retried.
			const isPriceNeeded = holding === null || (!holding.isHeld && holding.status !== 'past_due');
			const described = isPriceNeeded ? await storefront.pass() : undefined;

			if (!isDisposed) {
				setAnswer({ holding, pass: described === undefined || 'refusal' in described ? undefined : described });
			}
		});

		return () => {
			isDisposed = true;
		};
	}, [accountId, storefront]);

	if (storefront === undefined || answer === undefined) {
		return undefined;
	}

	const line = passLineOf(answer.holding, answer.pass);

	if (line === undefined) {
		return undefined;
	}

	/** Go where the store says, or say why not. */
	const goTo = (start: (returnUrl: string) => Promise<CheckoutOutcome>) => () => {
		setIsBusy(true);
		void start(window.location.href).then((outcome) => {
			if (outcome.kind === 'started') {
				navigate(outcome.url);

				return;
			}

			say({ kind: 'refused', line: CHECKOUT_REFUSED[outcome.refusal] });
			setIsBusy(false);
		});
	};

	const { holding, pass } = answer;
	const isPortalTheDoor = holding !== null && (holding.isHeld || holding.status === 'past_due');
	const action = isPortalTheDoor
		? { label: PASS_MANAGE_LABEL, onPress: goTo(async (returnUrl) => storefront.startPassPortal(returnUrl)) }
		: pass === undefined
			? undefined
			: { label: passLabelFor(pass), onPress: goTo(async (returnUrl) => storefront.startPassCheckout(returnUrl)) };

	return { ...(action === undefined ? {} : { action }), isBusy, line };
};
